import { useEffect, useMemo, useState } from 'react'
import { isAxiosError } from 'axios'
import { AlertCircle, FilePlus2, Loader2, Pencil, Plus, Save, Settings2, Trash2 } from 'lucide-react'
import { toast } from 'sonner'

import {
  createTemplate,
  getTemplateConfiguration,
  getTemplateConfigurationByVersion,
  getTemplates,
  updateTemplateConfiguration,
  type CreateTemplateInput,
  type TemplateFieldOption,
  type TemplateConfiguration,
  type TemplateField,
  type TemplateSection,
  type TemplateSubactivity,
  type TemplateSubactivityField,
  type TemplateSummary,
  type UpdateTemplateConfigurationInput,
  type UpdateTemplateFieldInput,
  type UpdateTemplateSubactivityFieldInput,
} from 'shared/api/templateService'
import { Alert, AlertDescription } from 'shared/ui/alert'
import { Badge } from 'shared/ui/badge'
import { Button } from 'shared/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from 'shared/ui/card'
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from 'shared/ui/dialog'
import { Input } from 'shared/ui/input'
import { Label } from 'shared/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'shared/ui/select'
import { Switch } from 'shared/ui/switch'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from 'shared/ui/table'
import { Tabs, TabsContent, TabsList, TabsTrigger } from 'shared/ui/tabs'
import { Textarea } from 'shared/ui/textarea'
import { DEFAULT_TENANT_ID } from 'shared/utils/tenantContext'

type DynamicFormConfigurationProps = {
  mode?: 'list' | 'editor'
  templateVersionId?: string | null
  onConfigure?: (templateVersionId: string) => void
  onBack?: () => void
}

type TemplateFeatureToggleKey =
  | 'generalEnabled'
  | 'subactivityEnabled'
  | 'workerEnabled'
  | 'mapEnabled'
  | 'ramEnabled'
  | 'attachmentEnabled'

const TEMPLATE_FEATURES: Array<{ key: TemplateFeatureToggleKey; label: string; hint: string }> = [
  { key: 'generalEnabled', label: 'General', hint: 'General permit information (hardcoded section)' },
  { key: 'subactivityEnabled', label: 'Subactivity', hint: 'Subactivity section' },
  { key: 'workerEnabled', label: 'Worker', hint: 'Worker section' },
  { key: 'mapEnabled', label: 'Map', hint: 'Work Area / Plot Plan' },
  { key: 'ramEnabled', label: 'RAM', hint: 'Risk Assessment Matrix' },
  { key: 'attachmentEnabled', label: 'Attachment', hint: 'Attachment section' },
]

const FIELD_TYPE_OPTIONS: Array<{ value: string; label: string }> = [
  { value: 'text', label: 'Text' },
  { value: 'textarea', label: 'Textarea' },
  { value: 'number', label: 'Number' },
  { value: 'date', label: 'Date' },
  { value: 'time', label: 'Time' },
  { value: 'attachment', label: 'Attachment' },
  { value: 'select', label: 'Select' },
  { value: 'checkbox', label: 'Checkbox' },
  { value: 'radio', label: 'Radio' },
  { value: 'search_select', label: 'Search Select' },
  { value: 'computed', label: 'Computed' },
]

const SELECT_INPUT_TYPE_OPTIONS: Array<{ value: 'single' | 'multiple'; label: string }> = [
  { value: 'single', label: 'Dropdownlist single selection' },
  { value: 'multiple', label: 'Dropdownlist multiple selection' },
]

const SUBACTIVITY_FIELD_TYPE_OPTIONS: Array<{ value: string; label: string }> = [
  { value: 'text', label: 'Text' },
  { value: 'textarea', label: 'Textarea' },
  { value: 'number', label: 'Number' },
  { value: 'date', label: 'Date' },
  { value: 'time', label: 'Time' },
  { value: 'select', label: 'Select' },
  { value: 'checkbox', label: 'Checkbox' },
  { value: 'radio', label: 'Radio' },
]

const OPTION_FIELD_TYPES = new Set(['select', 'multiselect', 'radio'])

const generateId = () => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID()
  }
  return `tmp-${Math.random().toString(36).slice(2, 11)}`
}

const parseOptionLines = (text: string): TemplateFieldOption[] =>
  text
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line, index) => ({
      optionId: generateId(),
      value: line,
      label: line,
      orderIndex: index + 1,
      isActive: true,
    }))

const optionsToText = (options: TemplateFieldOption[] | undefined) =>
  (options ?? []).map((option) => option.label ?? option.value).join('\n')

const parseDatasourceUiConfig = (value?: string | null): Record<string, unknown> => {
  if (!value || typeof value !== 'string') {
    return {}
  }
  try {
    const parsed = JSON.parse(value)
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed)
      ? { ...(parsed as Record<string, unknown>) }
      : {}
  } catch {
    return {}
  }
}

const resolveSearchSelectInputType = (value?: string | null): 'single' | 'multiple' => {
  const uiConfig = parseDatasourceUiConfig(value)
  return typeof uiConfig.inputType === 'string' && uiConfig.inputType.trim().toLowerCase() === 'multiple'
    ? 'multiple'
    : 'single'
}

const mergeDatasourceUiConfigInputType = (
  existingValue: string,
  inputType: 'single' | 'multiple',
  fieldType: string,
): string => {
  const trimmed = existingValue.trim()
  if (fieldType !== 'search_select') {
    return trimmed
  }

  const nextUiConfig = parseDatasourceUiConfig(trimmed)
  nextUiConfig.inputType = inputType
  return JSON.stringify(nextUiConfig, null, 2)
}

const getErrorMessage = (error: unknown, fallback: string) => {
  if (isAxiosError(error)) {
    const message = (error.response?.data as { message?: string } | undefined)?.message
    if (message?.trim()) return message.trim()
  }
  if (error instanceof Error && error.message.trim()) return error.message.trim()
  return fallback
}

const formatDateTime = (value?: string | null) => {
  if (!value) return '-'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return '-'
  return date.toLocaleString('th-TH')
}

const mapFieldPayload = (field: TemplateField): UpdateTemplateFieldInput => ({
  fieldId: field.fieldId,
  code: field.code,
  label: field.label,
  fieldType: field.fieldType,
  required: field.required,
  isKey: field.isKey,
  orderIndex: field.orderIndex,
  config: field.config ?? null,
  options: (field.options ?? []).map((option, index) => ({
    optionId: option.optionId,
    value: option.value,
    label: option.label,
    orderIndex: index + 1,
    isActive: option.isActive,
  })),
  datasource: field.datasource
    ? {
        datasourceId: field.datasource.id,
        sourceType: field.datasource.sourceType,
        tableName: field.datasource.tableName ?? null,
        valueColumn: field.datasource.valueColumn,
        labelColumn: field.datasource.labelColumn,
        whereSql: field.datasource.whereSql ?? null,
        orderBySql: field.datasource.orderBySql ?? null,
        paramsJson: field.datasource.paramsJson ?? null,
        uiConfigJson: field.datasource.uiConfigJson ?? null,
        isActive: field.datasource.isActive,
      }
    : null,
})

const mapSubactivityFieldPayload = (field: TemplateSubactivityField): UpdateTemplateSubactivityFieldInput => ({
  subactivityFieldId: field.subactivityFieldId,
  code: field.code,
  label: field.label,
  fieldType: field.fieldType,
  required: field.required,
  orderIndex: field.orderIndex,
  config: field.config ?? null,
  optionsJson: field.optionsJson ?? null,
  validationRegex: field.validationRegex ?? null,
})

export function DynamicFormConfiguration({
  mode = 'list',
  templateVersionId,
  onConfigure,
  onBack,
}: DynamicFormConfigurationProps) {
  const [templates, setTemplates] = useState<TemplateSummary[]>([])
  const [selectedTemplateVersionId, setSelectedTemplateVersionId] = useState('')

  const [configuration, setConfiguration] = useState<TemplateConfiguration | null>(null)
  const [isLoadingTemplates, setIsLoadingTemplates] = useState(false)
  const [isLoadingConfiguration, setIsLoadingConfiguration] = useState(false)
  const [isSaving, setIsSaving] = useState(false)

  const [templatesError, setTemplatesError] = useState<string | null>(null)
  const [configurationError, setConfigurationError] = useState<string | null>(null)

  const [isCreateTemplateOpen, setIsCreateTemplateOpen] = useState(false)
  const [isCreatingTemplate, setIsCreatingTemplate] = useState(false)
  const [createTemplatePayload, setCreateTemplatePayload] = useState<CreateTemplateInput>({
    tenantId: DEFAULT_TENANT_ID,
    permitCode: '',
    templateName: '',
    description: '',
    copyFromTemplateVersionId: null,
  })
  const [activeTab, setActiveTab] = useState<'general' | 'subactivity'>('general')

  const [fieldDialogOpen, setFieldDialogOpen] = useState(false)
  const [editingSectionId, setEditingSectionId] = useState<string>('')
  const [editingFieldId, setEditingFieldId] = useState<string | null>(null)
  const [fieldLabel, setFieldLabel] = useState('')
  const [fieldCode, setFieldCode] = useState('')
  const [fieldType, setFieldType] = useState<string>('text')
  const [fieldInputType, setFieldInputType] = useState<'single' | 'multiple'>('single')
  const [fieldRequired, setFieldRequired] = useState(false)
  const [fieldIsKey, setFieldIsKey] = useState(false)
  const [fieldConfig, setFieldConfig] = useState('')
  const [fieldOptionsText, setFieldOptionsText] = useState('')
  const [fieldOrderIndex, setFieldOrderIndex] = useState(0)
  const [useDatasource, setUseDatasource] = useState(false)
  const [datasourceSourceType, setDatasourceSourceType] = useState('sql')
  const [datasourceTableName, setDatasourceTableName] = useState('')
  const [datasourceValueColumn, setDatasourceValueColumn] = useState('')
  const [datasourceLabelColumn, setDatasourceLabelColumn] = useState('')
  const [datasourceWhereSql, setDatasourceWhereSql] = useState('')
  const [datasourceOrderSql, setDatasourceOrderSql] = useState('')
  const [datasourceParamsJson, setDatasourceParamsJson] = useState('')
  const [datasourceUiConfigJson, setDatasourceUiConfigJson] = useState('')
  const [datasourceId, setDatasourceId] = useState('')

  const [subFieldDialogOpen, setSubFieldDialogOpen] = useState(false)
  const [editingSubactivityId, setEditingSubactivityId] = useState<string>('')
  const [editingSubFieldId, setEditingSubFieldId] = useState<string | null>(null)
  const [subFieldLabel, setSubFieldLabel] = useState('')
  const [subFieldCode, setSubFieldCode] = useState('')
  const [subFieldType, setSubFieldType] = useState<string>('text')
  const [subFieldRequired, setSubFieldRequired] = useState(false)
  const [subFieldConfig, setSubFieldConfig] = useState('')
  const [subFieldOptionsText, setSubFieldOptionsText] = useState('')
  const [subFieldOrderIndex, setSubFieldOrderIndex] = useState(0)

  const selectedTemplate = useMemo(
    () => templates.find((item) => item.templateVersionId === selectedTemplateVersionId) ?? null,
    [templates, selectedTemplateVersionId],
  )

  const generalSections = useMemo(() => {
    const sections = configuration?.sections ?? []
    const filtered = sections.filter((section) => section.code?.toUpperCase().startsWith('GENERAL'))
    return filtered.length > 0 ? filtered : sections
  }, [configuration?.sections])

  const sortedSubactivities = useMemo(
    () => [...(configuration?.subActivities ?? [])].sort((a, b) => a.orderIndex - b.orderIndex),
    [configuration?.subActivities],
  )
  const normalizedFieldType = fieldType === 'select' && fieldInputType === 'multiple' ? 'multiselect' : fieldType
  const isComputedFieldType = normalizedFieldType === 'computed'
  const supportsDatasource =
    isComputedFieldType || OPTION_FIELD_TYPES.has(normalizedFieldType) || normalizedFieldType === 'search_select'
  const showOptionsEditor = !isComputedFieldType && OPTION_FIELD_TYPES.has(normalizedFieldType)
  const showSearchSelectAlert = !isComputedFieldType && normalizedFieldType === 'search_select'
  const showComputedDatasourceAlert = isComputedFieldType

  useEffect(() => {
    if (normalizedFieldType === 'computed') {
      setFieldRequired(false)
      setFieldIsKey(false)
      return
    }

    if (normalizedFieldType === 'search_select') {
      setUseDatasource(true)
    } else if (!OPTION_FIELD_TYPES.has(normalizedFieldType)) {
      setUseDatasource(false)
    }
  }, [normalizedFieldType])

  useEffect(() => {
    if (mode !== 'editor') {
      return
    }

    setSelectedTemplateVersionId(templateVersionId?.trim() ?? '')
  }, [mode, templateVersionId])

  const loadTemplates = async (preferredTemplateVersionId?: string) => {
    setIsLoadingTemplates(true)
    setTemplatesError(null)

    try {
      const items = await getTemplates(DEFAULT_TENANT_ID)
      setTemplates(items)

      if (items.length === 0) {
        setSelectedTemplateVersionId('')
        return
      }

      if (preferredTemplateVersionId && items.some((item) => item.templateVersionId === preferredTemplateVersionId)) {
        setSelectedTemplateVersionId(preferredTemplateVersionId)
        return
      }

      setSelectedTemplateVersionId((previous) => {
        if (previous && items.some((item) => item.templateVersionId === previous)) return previous
        return ''
      })
    } catch (error) {
      setTemplatesError(getErrorMessage(error, 'Unable to load templates.'))
    } finally {
      setIsLoadingTemplates(false)
    }
  }

  useEffect(() => {
    loadTemplates().catch(() => undefined)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    const loadConfiguration = async () => {
      if (mode !== 'editor') {
        setConfiguration(null)
        setConfigurationError(null)
        return
      }

      if (!selectedTemplateVersionId) {
        setConfiguration(null)
        return
      }

      setIsLoadingConfiguration(true)
      setConfigurationError(null)

      try {
        let result = await getTemplateConfigurationByVersion(DEFAULT_TENANT_ID, selectedTemplateVersionId)

        // Fallback for environments where by-version endpoint is not available yet.
        if (!result && selectedTemplate?.permitCode) {
          result = await getTemplateConfiguration(DEFAULT_TENANT_ID, selectedTemplate.permitCode)
        }

        setConfiguration(result)
        if (!result) {
          setConfigurationError('Template not found.')
        }
      } catch (error) {
        if (selectedTemplate?.permitCode) {
          try {
            const fallbackResult = await getTemplateConfiguration(DEFAULT_TENANT_ID, selectedTemplate.permitCode)
            setConfiguration(fallbackResult)
            setConfigurationError(fallbackResult ? null : 'Template not found.')
            return
          } catch {
            // Continue to show original by-version error below.
          }
        }

        setConfiguration(null)
        setConfigurationError(getErrorMessage(error, 'Unable to load template configuration.'))
      } finally {
        setIsLoadingConfiguration(false)
      }
    }

    loadConfiguration().catch(() => undefined)
  }, [mode, selectedTemplateVersionId, selectedTemplate?.permitCode])

  const handleCreateTemplate = async () => {
    if (!createTemplatePayload.permitCode.trim() || !createTemplatePayload.templateName.trim()) {
      toast.error('Please fill Permit Code and Template Name.')
      return
    }

    setIsCreatingTemplate(true)
    try {
      const created = await createTemplate({
        ...createTemplatePayload,
        tenantId: DEFAULT_TENANT_ID,
        permitCode: createTemplatePayload.permitCode.trim(),
        templateName: createTemplatePayload.templateName.trim(),
        description: createTemplatePayload.description?.trim() || null,
      })

      setIsCreateTemplateOpen(false)
      await loadTemplates(created.templateVersionId)
      toast.success('Template created successfully.')
    } catch (error) {
      toast.error(getErrorMessage(error, 'Unable to create template.'))
    } finally {
      setIsCreatingTemplate(false)
    }
  }

  const handleToggleFeature = (key: TemplateFeatureToggleKey, checked: boolean) => {
    setConfiguration((previous) => {
      if (!previous) return previous

      return {
        ...previous,
        [key]: checked,
      }
    })
  }

  const openAddFieldDialog = (sectionId: string) => {
    setEditingSectionId(sectionId)
    setEditingFieldId(null)
    setFieldLabel('')
    setFieldCode('')
    setFieldType('text')
    setFieldInputType('single')
    setFieldRequired(false)
    setFieldIsKey(false)
    setFieldConfig('')
    setFieldOptionsText('')
    setFieldOrderIndex(0)
    setUseDatasource(false)
    setDatasourceSourceType('sql')
    setDatasourceTableName('')
    setDatasourceValueColumn('')
    setDatasourceLabelColumn('')
    setDatasourceWhereSql('')
    setDatasourceOrderSql('')
    setDatasourceParamsJson('')
    setDatasourceUiConfigJson('')
    setDatasourceId('')
    setFieldDialogOpen(true)
  }

  const openEditFieldDialog = (sectionId: string, field: TemplateField) => {
    setEditingSectionId(sectionId)
    setEditingFieldId(field.fieldId)
    setFieldLabel(field.label ?? '')
    setFieldCode(field.code ?? '')
    setFieldType(field.fieldType === 'multiselect' ? 'select' : (field.fieldType ?? 'text'))
    setFieldInputType(
      field.fieldType === 'multiselect'
        ? 'multiple'
        : field.fieldType === 'search_select'
          ? resolveSearchSelectInputType(field.datasource?.uiConfigJson)
          : 'single',
    )
    setFieldRequired(Boolean(field.required))
    setFieldIsKey(Boolean(field.isKey))
    setFieldConfig(field.config ?? '')
    setFieldOptionsText(optionsToText(field.options))
    setFieldOrderIndex(field.orderIndex ?? 0)
    setUseDatasource(Boolean(field.datasource))
    setDatasourceSourceType(field.datasource?.sourceType ?? 'sql')
    setDatasourceTableName(field.datasource?.tableName ?? '')
    setDatasourceValueColumn(field.datasource?.valueColumn ?? '')
    setDatasourceLabelColumn(field.datasource?.labelColumn ?? '')
    setDatasourceWhereSql(field.datasource?.whereSql ?? '')
    setDatasourceOrderSql(field.datasource?.orderBySql ?? '')
    setDatasourceParamsJson(field.datasource?.paramsJson ?? '')
    setDatasourceUiConfigJson(field.datasource?.uiConfigJson ?? '')
    setDatasourceId(field.datasource?.id ?? '')
    setFieldDialogOpen(true)
  }

  const upsertField = () => {
    if (!configuration) return
    if (!editingSectionId || !fieldLabel.trim() || !fieldCode.trim()) {
      toast.error('Please complete the required field details.')
      return
    }

    setConfiguration((previous) => {
      if (!previous) return previous
      return {
        ...previous,
        sections: previous.sections.map((section) => {
          if (section.templateSectionId !== editingSectionId) return section
          const existing = section.fields.find((f) => f.fieldId === editingFieldId)
          const baseOrder =
            fieldOrderIndex && fieldOrderIndex > 0
              ? fieldOrderIndex
              : (existing?.orderIndex ?? section.fields.length + 1)

          let nextDatasource: TemplateField['datasource'] = existing?.datasource ?? null
          if (supportsDatasource && useDatasource) {
            const valueColumn = datasourceValueColumn.trim()
            const labelColumn = datasourceLabelColumn.trim()
            if (!valueColumn || !labelColumn) {
              toast.error('Please provide both Value Column and Label Column for the datasource.')
              return section
            }
            nextDatasource = {
              id: datasourceId.trim() || existing?.datasource?.id || generateId(),
              sourceType: datasourceSourceType.trim() || 'sql',
              tableName: datasourceTableName.trim() || undefined,
              valueColumn,
              labelColumn,
              whereSql: datasourceWhereSql.trim() || undefined,
              orderBySql: datasourceOrderSql.trim() || undefined,
              paramsJson: datasourceParamsJson.trim() || undefined,
              uiConfigJson:
                mergeDatasourceUiConfigInputType(
                  datasourceUiConfigJson,
                  fieldInputType,
                  normalizedFieldType,
                ) || undefined,
              isActive: true,
              searchableColumns: existing?.datasource?.searchableColumns,
              displayColumns: existing?.datasource?.displayColumns,
              items: existing?.datasource?.items ?? [],
            }
          } else {
            nextDatasource = null
          }

          const nextField: TemplateField = {
            fieldId: editingFieldId ?? generateId(),
            label: fieldLabel.trim(),
            code: fieldCode.trim().toUpperCase(),
            fieldType: normalizedFieldType,
            required: isComputedFieldType ? false : fieldRequired,
            isKey: isComputedFieldType ? false : fieldIsKey,
            orderIndex: baseOrder,
            config: fieldConfig.trim() || null,
            validation: existing?.validation ?? null,
            visibilityRules: existing?.visibilityRules ?? null,
            visible: existing?.visible ?? true,
            layout: existing?.layout ?? null,
            sourceType: existing?.sourceType ?? null,
            sourceRef: existing?.sourceRef ?? null,
            sourceParams: existing?.sourceParams ?? null,
            options: showOptionsEditor
              ? parseOptionLines(fieldOptionsText)
              : [],
            datasource: nextDatasource,
          }
          const existingIndex = section.fields.findIndex((f) => f.fieldId === nextField.fieldId)
          const fields =
            existingIndex >= 0
              ? section.fields.map((f, idx) => (idx === existingIndex ? { ...f, ...nextField } : f))
              : [...section.fields, { ...nextField }]
          const ordered = [...fields].sort((a, b) => (a.orderIndex ?? 9999) - (b.orderIndex ?? 9999))

          return {
            ...section,
            fields: ordered.map((field, index) => ({ ...field, orderIndex: index + 1 })),
          }
        }),
      }
    })

    setFieldDialogOpen(false)
  }

  const deleteField = (sectionId: string, fieldId: string) => {
    setConfiguration((previous) => {
      if (!previous) return previous
      return {
        ...previous,
        sections: previous.sections.map((section) => {
          if (section.templateSectionId !== sectionId) return section
          const fields = section.fields.filter((field) => field.fieldId !== fieldId)
          return {
            ...section,
            fields: fields.map((field, index) => ({ ...field, orderIndex: index + 1 })),
          }
        }),
      }
    })
  }

  const openAddSubFieldDialog = (subactivityId: string) => {
    setEditingSubactivityId(subactivityId)
    setEditingSubFieldId(null)
    setSubFieldLabel('')
    setSubFieldCode('')
    setSubFieldType('text')
    setSubFieldRequired(false)
    setSubFieldConfig('')
    setSubFieldOptionsText('')
    setSubFieldOrderIndex(0)
    setSubFieldDialogOpen(true)
  }

  const openEditSubFieldDialog = (subactivityId: string, field: TemplateSubactivityField) => {
    setEditingSubactivityId(subactivityId)
    setEditingSubFieldId(field.subactivityFieldId)
    setSubFieldLabel(field.label ?? '')
    setSubFieldCode(field.code ?? '')
    setSubFieldType(field.fieldType ?? 'text')
    setSubFieldRequired(Boolean(field.required))
    setSubFieldConfig(field.config ?? '')
    setSubFieldOrderIndex(field.orderIndex ?? 0)
    setSubFieldOptionsText(
      (() => {
        if (!field.optionsJson) return ''
        try {
          const parsed = JSON.parse(field.optionsJson) as Array<{ label?: string; value?: string }>
          return parsed.map((item) => item.label ?? item.value ?? '').filter(Boolean).join('\n')
        } catch {
          return ''
        }
      })(),
    )
    setSubFieldDialogOpen(true)
  }

  const upsertSubField = () => {
    if (!configuration) return
    if (!editingSubactivityId || !subFieldLabel.trim() || !subFieldCode.trim()) {
      toast.error('Please complete the required field details.')
      return
    }

    const optionsJson = OPTION_FIELD_TYPES.has(subFieldType)
      ? JSON.stringify(
          parseOptionLines(subFieldOptionsText).map((item) => ({ label: item.label, value: item.value })),
        )
      : null

    setConfiguration((previous) => {
      if (!previous) return previous
      return {
        ...previous,
        subActivities: previous.subActivities.map((sub) => {
          if (sub.subactivityId !== editingSubactivityId) return sub
          const existing = sub.fields.find((f) => f.subactivityFieldId === editingSubFieldId)
          const baseOrder =
            subFieldOrderIndex && subFieldOrderIndex > 0
              ? subFieldOrderIndex
              : (existing?.orderIndex ?? sub.fields.length + 1)
          const nextField: TemplateSubactivityField = {
            subactivityFieldId: editingSubFieldId ?? generateId(),
            label: subFieldLabel.trim(),
            code: subFieldCode.trim().toUpperCase(),
            fieldType: subFieldType,
            required: subFieldRequired,
            orderIndex: baseOrder,
            config: subFieldConfig.trim() || null,
            optionsJson,
            validationRegex: existing?.validationRegex ?? null,
          }
          const existingIndex = sub.fields.findIndex((f) => f.subactivityFieldId === nextField.subactivityFieldId)
          const fields =
            existingIndex >= 0
              ? sub.fields.map((f, idx) => (idx === existingIndex ? { ...f, ...nextField } : f))
              : [...sub.fields, { ...nextField }]
          const ordered = [...fields].sort((a, b) => (a.orderIndex ?? 9999) - (b.orderIndex ?? 9999))

          return {
            ...sub,
            fields: ordered.map((field, index) => ({ ...field, orderIndex: index + 1 })),
          }
        }),
      }
    })

    setSubFieldDialogOpen(false)
  }

  const deleteSubField = (subactivityId: string, subFieldId: string) => {
    setConfiguration((previous) => {
      if (!previous) return previous
      return {
        ...previous,
        subActivities: previous.subActivities.map((sub) => {
          if (sub.subactivityId !== subactivityId) return sub
          const fields = sub.fields.filter((field) => field.subactivityFieldId !== subFieldId)
          return {
            ...sub,
            fields: fields.map((field, index) => ({ ...field, orderIndex: index + 1 })),
          }
        }),
      }
    })
  }

  const handleSave = async () => {
    if (!configuration) {
      toast.error('Please select a template first.')
      return
    }

    const payload: UpdateTemplateConfigurationInput = {
      tenantId: DEFAULT_TENANT_ID,
      templateId: configuration.templateId,
      templateVersionId: configuration.templateVersionId,
      permitCode: configuration.permitCode,
      generalEnabled: configuration.generalEnabled ?? true,
      subactivityEnabled: configuration.subactivityEnabled ?? true,
      workerEnabled: configuration.workerEnabled ?? true,
      mapEnabled: configuration.mapEnabled ?? true,
      ramEnabled: configuration.ramEnabled ?? true,
      attachmentEnabled: configuration.attachmentEnabled ?? true,
      sections: configuration.sections.map((section) => ({
        templateSectionId: section.templateSectionId,
        fields: [...section.fields]
          .sort((a, b) => a.orderIndex - b.orderIndex)
          .map(mapFieldPayload),
      })),
      subActivities: configuration.subActivities.map((subactivity) => ({
        subactivityId: subactivity.subactivityId,
        fields: [...subactivity.fields]
          .sort((a, b) => a.orderIndex - b.orderIndex)
          .map(mapSubactivityFieldPayload),
      })),
    }

    setIsSaving(true)
    try {
      await updateTemplateConfiguration(payload)
      toast.success('Template configuration saved.')
    } catch (error) {
      toast.error(getErrorMessage(error, 'Unable to save template configuration.'))
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <CardTitle className="flex items-center gap-2 text-2xl">
                <Settings2 className="h-6 w-6 text-blue-600" />
                {mode === 'editor' ? 'Dynamic Form Template Editor' : 'Dynamic Form Configuration'}
              </CardTitle>
              <p className="text-sm text-muted-foreground">
                {mode === 'editor'
                  ? 'Edit section visibility and template structure.'
                  : 'Select template to open a separate configuration page.'}
              </p>
            </div>
            <div className="flex items-center gap-2">
              {mode === 'editor' ? (
                <Button variant="ghost" onClick={() => onBack?.()}>
                  Back to Template List
                </Button>
              ) : null}
              <Button
                variant="outline"
                onClick={() => {
                  setCreateTemplatePayload({
                    tenantId: DEFAULT_TENANT_ID,
                    permitCode: '',
                    templateName: '',
                    description: '',
                    copyFromTemplateVersionId: null,
                  })
                  setIsCreateTemplateOpen(true)
                }}
              >
                <FilePlus2 className="mr-2 h-4 w-4" />
                Create Template
              </Button>
              {mode === 'editor' ? (
                <Button onClick={handleSave} disabled={!configuration || isSaving || isLoadingConfiguration}>
                  {isSaving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
                  Save
                </Button>
              ) : null}
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {templatesError && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{templatesError}</AlertDescription>
            </Alert>
          )}
          {configurationError && mode === 'editor' ? (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{configurationError}</AlertDescription>
            </Alert>
          ) : null}
        </CardContent>
      </Card>

      {mode === 'list' ? (
        <Card>
          <CardHeader>
            <CardTitle>Template List</CardTitle>
          </CardHeader>
          <CardContent>
            {isLoadingTemplates ? (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" />
                Loading templates...
              </div>
            ) : templates.length === 0 ? (
              <p className="text-sm text-muted-foreground">No templates found.</p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Permit Code</TableHead>
                    <TableHead>Template Name</TableHead>
                    <TableHead>Version</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Created At</TableHead>
                    <TableHead className="text-right">Action</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {templates.map((item) => (
                    <TableRow key={item.templateVersionId}>
                      <TableCell className="font-medium">{item.permitCode}</TableCell>
                      <TableCell>{item.templateName}</TableCell>
                      <TableCell>v{item.version}</TableCell>
                      <TableCell>
                        <Badge variant={item.isTemplateActive ? 'default' : 'secondary'}>
                          {item.status}
                        </Badge>
                      </TableCell>
                      <TableCell>{formatDateTime(item.createdAt)}</TableCell>
                      <TableCell className="text-right">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            if (onConfigure) {
                              onConfigure(item.templateVersionId)
                              return
                            }
                            setSelectedTemplateVersionId(item.templateVersionId)
                          }}
                        >
                          Configure
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      ) : isLoadingConfiguration ? (
        <Card>
          <CardContent className="flex items-center gap-2 p-6 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            Loading configuration...
          </CardContent>
        </Card>
      ) : configuration ? (
        <>
          <Card>
            <CardContent className="rounded-md border bg-muted/20 p-4">
              <div className="grid gap-3 lg:grid-cols-3">
                <div className="rounded-md border bg-background p-3 text-sm lg:col-span-1">
                  <p><span className="font-medium">Permit Code:</span> {selectedTemplate?.permitCode ?? configuration.permitCode}</p>
                  <p><span className="font-medium">Template Name:</span> {selectedTemplate?.templateName ?? configuration.templateName}</p>
                  <p><span className="font-medium">Version:</span> {selectedTemplate?.version ?? configuration.version}</p>
                  <p><span className="font-medium">Status:</span> {selectedTemplate?.status ?? 'ACTIVE'}</p>
                </div>
                <div className="space-y-2 lg:col-span-2">
                  <p className="text-xs font-medium text-muted-foreground">Template Version Features</p>
                  <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
                    {TEMPLATE_FEATURES.map((feature) => (
                      <div key={feature.key} className="flex items-center justify-between rounded-md border bg-background px-3 py-2">
                        <span className="text-sm font-medium">{feature.label}</span>
                        <Switch
                          checked={configuration[feature.key] ?? true}
                          onCheckedChange={(checked) => handleToggleFeature(feature.key, checked)}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          <Tabs value={activeTab} onValueChange={(value) => setActiveTab(value as 'general' | 'subactivity')}>
            <TabsList>
              <TabsTrigger value="general">General Sections</TabsTrigger>
              <TabsTrigger value="subactivity">Sub Activities</TabsTrigger>
            </TabsList>

            <TabsContent value="general" className="space-y-4">
              {generalSections.length === 0 ? (
                <Alert>
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>No section found.</AlertDescription>
                </Alert>
              ) : (
                generalSections.map((section: TemplateSection) => (
                  <Card key={section.templateSectionId}>
                    <CardHeader className="flex flex-row items-start justify-between space-y-0">
                      <div className="space-y-1">
                        <CardTitle className="text-base font-semibold">
                          {section.title || section.code}{' '}
                          <Badge variant="secondary" className="align-middle text-xs font-medium uppercase">
                            {section.code}
                          </Badge>
                        </CardTitle>
                        <p className="text-sm text-muted-foreground">{section.fields.length} fields</p>
                      </div>
                      <Button size="sm" onClick={() => openAddFieldDialog(section.templateSectionId)}>
                        <Plus className="mr-2 h-4 w-4" />
                        Add Field
                      </Button>
                    </CardHeader>
                    <CardContent>
                      {section.fields.length === 0 ? (
                        <Alert className="border-dashed">
                          <AlertCircle className="h-4 w-4" />
                          <AlertDescription>No fields.</AlertDescription>
                        </Alert>
                      ) : (
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead>Label</TableHead>
                              <TableHead>Code</TableHead>
                              <TableHead className="w-36">Type</TableHead>
                              <TableHead className="w-24 text-center">Required</TableHead>
                              <TableHead className="w-24 text-center">Key</TableHead>
                              <TableHead className="w-28 text-right">Action</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {section.fields.map((field) => (
                              <TableRow key={field.fieldId}>
                                <TableCell className="max-w-xs">
                                  <div className="flex flex-col">
                                    <span className="font-medium">{field.label || field.code}</span>
                                    {field.config ? (
                                      <span className="text-xs text-muted-foreground">{field.config}</span>
                                    ) : null}
                                  </div>
                                </TableCell>
                                <TableCell className="text-sm text-muted-foreground">{field.code}</TableCell>
                                <TableCell>
                                  <Badge variant="outline" className="uppercase">
                                    {field.fieldType}
                                  </Badge>
                                </TableCell>
                                <TableCell className="text-center">
                                  {field.required ? (
                                    <span className="text-xs font-semibold text-emerald-600">Yes</span>
                                  ) : (
                                    <span className="text-xs text-muted-foreground">No</span>
                                  )}
                                </TableCell>
                                <TableCell className="text-center">
                                  {field.isKey ? (
                                    <span className="text-xs font-semibold text-primary">Key</span>
                                  ) : (
                                    <span className="text-xs text-muted-foreground">-</span>
                                  )}
                                </TableCell>
                                <TableCell className="flex items-center justify-end gap-1">
                                  <Button
                                    size="icon"
                                    variant="ghost"
                                    onClick={() => openEditFieldDialog(section.templateSectionId, field)}
                                  >
                                    <Pencil className="h-4 w-4" />
                                  </Button>
                                  <Button
                                    size="icon"
                                    variant="ghost"
                                    className="text-destructive"
                                    onClick={() => deleteField(section.templateSectionId, field.fieldId)}
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </Button>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      )}
                    </CardContent>
                  </Card>
                ))
              )}
            </TabsContent>

            <TabsContent value="subactivity" className="space-y-4">
              {sortedSubactivities.length === 0 ? (
                <Alert>
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>No subactivity found.</AlertDescription>
                </Alert>
              ) : (
                sortedSubactivities.map((subactivity: TemplateSubactivity) => (
                  <Card key={subactivity.subactivityId}>
                    <CardHeader className="flex flex-row items-start justify-between space-y-0">
                      <div className="space-y-1">
                        <CardTitle className="text-base font-semibold">
                          {subactivity.name}{' '}
                          <Badge variant="secondary" className="align-middle text-xs font-medium uppercase">
                            {subactivity.code}
                          </Badge>
                        </CardTitle>
                        <p className="text-sm text-muted-foreground">{subactivity.fields.length} fields</p>
                      </div>
                      <Button size="sm" onClick={() => openAddSubFieldDialog(subactivity.subactivityId)}>
                        <Plus className="mr-2 h-4 w-4" />
                        Add Field
                      </Button>
                    </CardHeader>
                    <CardContent>
                      {subactivity.fields.length === 0 ? (
                        <Alert className="border-dashed">
                          <AlertCircle className="h-4 w-4" />
                          <AlertDescription>No fields.</AlertDescription>
                        </Alert>
                      ) : (
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead>Label</TableHead>
                              <TableHead>Code</TableHead>
                              <TableHead className="w-36">Type</TableHead>
                              <TableHead className="w-24 text-center">Required</TableHead>
                              <TableHead className="w-28 text-right">Action</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {subactivity.fields.map((field) => (
                              <TableRow key={field.subactivityFieldId}>
                                <TableCell className="max-w-xs">
                                  <div className="flex flex-col">
                                    <span className="font-medium">{field.label || field.code}</span>
                                    {field.config ? (
                                      <span className="text-xs text-muted-foreground">{field.config}</span>
                                    ) : null}
                                  </div>
                                </TableCell>
                                <TableCell className="text-sm text-muted-foreground">{field.code}</TableCell>
                                <TableCell>
                                  <Badge variant="outline" className="uppercase">
                                    {field.fieldType}
                                  </Badge>
                                </TableCell>
                                <TableCell className="text-center">
                                  {field.required ? (
                                    <span className="text-xs font-semibold text-emerald-600">Yes</span>
                                  ) : (
                                    <span className="text-xs text-muted-foreground">No</span>
                                  )}
                                </TableCell>
                                <TableCell className="flex items-center justify-end gap-1">
                                  <Button
                                    size="icon"
                                    variant="ghost"
                                    onClick={() => openEditSubFieldDialog(subactivity.subactivityId, field)}
                                  >
                                    <Pencil className="h-4 w-4" />
                                  </Button>
                                  <Button
                                    size="icon"
                                    variant="ghost"
                                    className="text-destructive"
                                    onClick={() => deleteSubField(subactivity.subactivityId, field.subactivityFieldId)}
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </Button>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      )}
                    </CardContent>
                  </Card>
                ))
              )}
            </TabsContent>
          </Tabs>
        </>
      ) : (
        <Card>
          <CardContent className="p-6 text-sm text-muted-foreground">Template not found.</CardContent>
        </Card>
      )}

      <Dialog open={fieldDialogOpen} onOpenChange={setFieldDialogOpen}>
        <DialogContent className="max-h-[85vh] max-w-3xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingFieldId ? 'Edit Field' : 'Add Field'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-6">
            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label>Label *</Label>
                <Input value={fieldLabel} onChange={(event) => setFieldLabel(event.target.value)} />
              </div>
              <div className="space-y-2">
                <Label>Code *</Label>
                <Input value={fieldCode} onChange={(event) => setFieldCode(event.target.value.toUpperCase())} />
              </div>
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label>Type</Label>
                <Select value={fieldType} onValueChange={setFieldType}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {FIELD_TYPE_OPTIONS.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {fieldType === 'select' || fieldType === 'search_select' ? (
                <div className="space-y-2">
                  <Label>Input Type</Label>
                  <Select value={fieldInputType} onValueChange={(value) => setFieldInputType(value as 'single' | 'multiple')}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {SELECT_INPUT_TYPE_OPTIONS.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              ) : (
              <div className="space-y-2">
                <Label>Order</Label>
                <Input
                  type="number"
                  min={1}
                  value={fieldOrderIndex === 0 ? '' : fieldOrderIndex}
                  onChange={(event) => setFieldOrderIndex(Number(event.target.value))}
                  placeholder="Auto"
                />
              </div>
              )}
            </div>

            {fieldType === 'select' || fieldType === 'search_select' ? (
              <div className="space-y-2">
                <Label>Order</Label>
                <Input
                  type="number"
                  min={1}
                  value={fieldOrderIndex === 0 ? '' : fieldOrderIndex}
                  onChange={(event) => setFieldOrderIndex(Number(event.target.value))}
                  placeholder="Auto"
                />
              </div>
            ) : null}

            <div className="space-y-2">
              <Label>Field Config JSON</Label>
              <Textarea
                rows={6}
                value={fieldConfig}
                onChange={(event) => setFieldConfig(event.target.value)}
                className="placeholder:text-slate-400"
                placeholder={`{
  "optionMessages": [
    {
      "code": "URGENT",
      "variant": "danger",
      "title": "Additional Information",
      "messages": ["Message 1", "Message 2"]
    }
  ]
}`}
              />
              <p className="text-xs text-muted-foreground">
                Use JSON to configure field behavior such as information messages shown after a selected option code is matched.
              </p>
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              <div className="flex items-center justify-between rounded-md border p-3">
                <span className="text-sm">Required</span>
                <Switch checked={fieldRequired} onCheckedChange={setFieldRequired} />
              </div>
              <div className="flex items-center justify-between rounded-md border p-3">
                <span className="text-sm">Is Key</span>
                <Switch checked={fieldIsKey} onCheckedChange={setFieldIsKey} />
              </div>
            </div>

            {showOptionsEditor ? (
              <div className="space-y-2">
                <Label>Options (one line per item)</Label>
                <Textarea rows={4} value={fieldOptionsText} onChange={(event) => setFieldOptionsText(event.target.value)} />
              </div>
            ) : null}

            {showSearchSelectAlert ? (
              <Alert>
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  Search Select should define a Data Source so users can search and load options correctly.
                </AlertDescription>
              </Alert>
            ) : null}

            {showComputedDatasourceAlert ? (
              <Alert>
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  Computed field should define a Data Source so the system can calculate and save the value.
                </AlertDescription>
              </Alert>
            ) : null}

            {supportsDatasource ? (
              <div className="space-y-4 rounded-md border border-dashed p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium">Data Source</p>
                    <p className="text-xs text-muted-foreground">
                      {isComputedFieldType
                        ? 'Define the SQL/function and parameter mapping used to calculate this field automatically.'
                        : 'Enable this to load dropdown/search-select options from a datasource.'}
                    </p>
                  </div>
                  <Switch
                    checked={useDatasource}
                    onCheckedChange={(checked) => setUseDatasource(normalizedFieldType === 'search_select' ? true : checked)}
                    disabled={normalizedFieldType === 'search_select'}
                  />
                </div>

                {useDatasource ? (
                  <>
                    <div className="grid gap-4 md:grid-cols-2">
                      <div className="space-y-2">
                        <Label>Source Type</Label>
                        <Select value={datasourceSourceType} onValueChange={setDatasourceSourceType}>
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="sql">SQL</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label>{isComputedFieldType ? 'SQL / Function' : 'Table/View'}</Label>
                        <Input
                          value={datasourceTableName}
                          placeholder={isComputedFieldType ? "e.g. ptw.ufn_getAdvanceDays(@start_at, @end_at)" : "e.g. ptw.view_work_locations"}
                          onChange={(event) => setDatasourceTableName(event.target.value)}
                        />
                      </div>
                    </div>

                    <div className="grid gap-4 md:grid-cols-2">
                      <div className="space-y-2">
                        <Label>Value Column *</Label>
                        <Input
                          value={datasourceValueColumn}
                          placeholder={isComputedFieldType ? "e.g. result" : "e.g. location_id"}
                          onChange={(event) => setDatasourceValueColumn(event.target.value)}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Label Column *</Label>
                        <Input
                          value={datasourceLabelColumn}
                          placeholder={isComputedFieldType ? "e.g. result" : "e.g. location_name"}
                          onChange={(event) => setDatasourceLabelColumn(event.target.value)}
                        />
                      </div>
                    </div>

                    <div className="grid gap-4 md:grid-cols-2">
                      <div className="space-y-2">
                        <Label>Where Clause</Label>
                        <Input
                          value={datasourceWhereSql}
                          placeholder="e.g. is_active = 1"
                          onChange={(event) => setDatasourceWhereSql(event.target.value)}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Order By</Label>
                        <Input
                          value={datasourceOrderSql}
                          placeholder="e.g. location_name ASC"
                          onChange={(event) => setDatasourceOrderSql(event.target.value)}
                        />
                      </div>
                    </div>

                    <div className="grid gap-4 md:grid-cols-2">
                      <div className="space-y-2">
                        <Label>Params JSON</Label>
                        <Textarea
                          rows={3}
                          value={datasourceParamsJson}
                          onChange={(event) => setDatasourceParamsJson(event.target.value)}
                          placeholder={
                            isComputedFieldType
                              ? '{"start_at":"@start_at","end_at":"@end_at"}'
                              : '{"tenant":"@tenant"}'
                          }
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>UI Config JSON</Label>
                        <Textarea
                          rows={8}
                          value={datasourceUiConfigJson}
                          onChange={(event) => setDatasourceUiConfigJson(event.target.value)}
                          className="placeholder:text-slate-400"
                          placeholder={`{
  "placeholder": "Search...",
  "inputType": "single",
  "remoteSearchOnly": true,
  "minSearchLength": 2,
  "defaultCode": "URGENT",
  "defaultMatchColumn": "code"
}`}
                        />
                        <div className="space-y-1 text-xs text-muted-foreground">
                          <p>Supported keys: <code>placeholder</code>, <code>inputType</code>, <code>remoteSearchOnly</code>, <code>minSearchLength</code>, <code>defaultCode</code>, <code>defaultCodes</code>, <code>defaultMatchColumn</code>.</p>
                          <p><code>defaultCode</code> / <code>defaultCodes</code> are recommended when the real field value is an ID but the datasource also has a readable code column.</p>
                          <p><code>defaultMatchColumn</code> is optional and defaults to <code>code</code>.</p>
                          <p>Legacy <code>defaultValue</code> / <code>defaultValues</code> still work when you want to match the actual datasource value directly.</p>
                          <p>Default values are applied on the request form only when the field is still empty.</p>
                        </div>
                      </div>
                    </div>
                  </>
                ) : (
                  <p className="text-xs text-muted-foreground">
                    If Data Source is disabled, the field will use the static options configured on this field.
                  </p>
                )}
              </div>
            ) : null}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setFieldDialogOpen(false)}>Cancel</Button>
            <Button onClick={upsertField}>Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={subFieldDialogOpen} onOpenChange={setSubFieldDialogOpen}>
        <DialogContent className="max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingSubFieldId ? 'Edit Subactivity Field' : 'Add Subactivity Field'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid gap-3 md:grid-cols-2">
              <div className="space-y-2">
                <Label>Label *</Label>
                <Input value={subFieldLabel} onChange={(event) => setSubFieldLabel(event.target.value)} />
              </div>
              <div className="space-y-2">
                <Label>Code *</Label>
                <Input value={subFieldCode} onChange={(event) => setSubFieldCode(event.target.value.toUpperCase())} />
              </div>
            </div>
            <div className="grid gap-3 md:grid-cols-2">
              <div className="space-y-2">
                <Label>Type</Label>
                <Select value={subFieldType} onValueChange={setSubFieldType}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {SUBACTIVITY_FIELD_TYPE_OPTIONS.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Order</Label>
                <Input
                  type="number"
                  min={1}
                  value={subFieldOrderIndex === 0 ? '' : subFieldOrderIndex}
                  onChange={(event) => setSubFieldOrderIndex(Number(event.target.value))}
                  placeholder="Auto"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Helper Text</Label>
              <Input value={subFieldConfig} onChange={(event) => setSubFieldConfig(event.target.value)} />
            </div>
            <div className="flex items-center justify-between rounded-md border p-3">
              <span className="text-sm">Required</span>
              <Switch checked={subFieldRequired} onCheckedChange={setSubFieldRequired} />
            </div>
            {OPTION_FIELD_TYPES.has(subFieldType) ? (
              <div className="space-y-2">
                <Label>Options (one line per item)</Label>
                <Textarea rows={4} value={subFieldOptionsText} onChange={(event) => setSubFieldOptionsText(event.target.value)} />
              </div>
            ) : null}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSubFieldDialogOpen(false)}>Cancel</Button>
            <Button onClick={upsertSubField}>Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={isCreateTemplateOpen} onOpenChange={setIsCreateTemplateOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create Template</DialogTitle>
          </DialogHeader>

          <div className="space-y-3">
            <div className="space-y-2">
              <Label>Permit Code *</Label>
              <Input value={createTemplatePayload.permitCode} onChange={(event) => setCreateTemplatePayload((p) => ({ ...p, permitCode: event.target.value }))} placeholder="e.g. COLD_WORK" />
            </div>
            <div className="space-y-2">
              <Label>Template Name *</Label>
              <Input value={createTemplatePayload.templateName} onChange={(event) => setCreateTemplatePayload((p) => ({ ...p, templateName: event.target.value }))} placeholder="e.g. Cold Work Permit" />
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea rows={3} value={createTemplatePayload.description ?? ''} onChange={(event) => setCreateTemplatePayload((p) => ({ ...p, description: event.target.value }))} />
            </div>
            <p className="text-xs text-muted-foreground">
              Copy template is temporarily disabled. New template will start with default sections.
            </p>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsCreateTemplateOpen(false)} disabled={isCreatingTemplate}>Cancel</Button>
            <Button onClick={handleCreateTemplate} disabled={isCreatingTemplate}>{isCreatingTemplate ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}Create</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}

export default DynamicFormConfiguration

